import React, { ReactElement, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Form, message, Typography } from "antd"
import ModalCard from "@/UI/Modals/ModalCard"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClone, faExclamationCircle } from "@fortawesome/free-solid-svg-icons"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import { isEmpty } from "lodash"
import { PhoneFormItem, SenderNameFormItem } from "@/UI"
import { EmailFormItem } from "@/UI/Forms/FormItems/EmailFormItem"
import useInputPhoneNumber from "@/hooks/useInputPhoneNumber"
import DraftPickupCell from "@/modules/new-orders/DraftOrderTable/cells/DraftPickupCell/DraftPickupCell.component"
import { Client, CreateClientPayload } from "@/types/clients.types"
import { TimeAtStopFormItem } from "@/UI/Forms/FormItems/TimeAtStopFormItem"
import { DayTimeRangeForm } from "@/UI/Forms/FormItems/DayTimeRange"
import { DateTime } from "luxon"

interface IClientsAddEditModalProps {
  title: string
  button: ReactElement
  client: Client | null
  isOpen: boolean
  handleCreateClient: (client: CreateClientPayload) => Promise<void>
  handleUpdateClient: (client: Client) => Promise<void>
  fetchClients: () => Promise<void>
  onClose?: () => void
}

const ClientsAddEditModal: React.FC<IClientsAddEditModalProps> = ({
  title,
  button,
  client,
  isOpen,
  fetchClients,
  handleCreateClient,
  handleUpdateClient,
  onClose,
}) => {
  const { t } = useTranslation()
  const { Paragraph } = Typography
  const [form] = Form.useForm()

  const [isModalVisible, setIsModalVisible] = useState<boolean>(isOpen)
  const [createdClient, setCreatedClient] = useState<Client | null>()
  const { countryCode, handlePhoneSelect } = useInputPhoneNumber(form, "phone")

  const openModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    setIsModalVisible(true)
  }
  const closeModal = () => {
    setIsModalVisible(false)
    if (onClose) {
      onClose()
    }
  }

  const onFinish = async (values: CreateClientPayload) => {
    try {
      const locationId = form.getFieldValue("locationId")
      const locationIds = locationId ? [locationId] : []
      const arrivalTimeStart = form.getFieldValue("expectedArrival")?.from?.toFormat("HH:mm")
      const arrivalTimeEnd = form.getFieldValue("expectedArrival")?.to?.toFormat("HH:mm")
      const timeAtStop = Number(values.timeAtStop)
      if (client) {
        await handleUpdateClient({
          ...client,
          ...values,
          locationIds,
          arrivalTimes: {
            start: arrivalTimeStart,
            end: arrivalTimeEnd,
          },
          timeAtStop,
        })
      } else {
        await handleCreateClient({
          ...values,
          locationIds,
          arrivalTimes: {
            start: arrivalTimeStart,
            end: arrivalTimeEnd,
          },
          timeAtStop,
        })
      }
      handleClose()
    } catch (error) {
      message.error(t("profile.Clients.createClientError"))
    }
  }

  const handleClose = () => {
    if (createdClient) {
      fetchClients()
    }
    setCreatedClient(null)
    form.resetFields()
    closeModal()
  }

  const handleLocationSelect = async (locationId?: string, _?: string) => {
    form.setFieldsValue({ locationId })
  }

  useEffect(() => {
    if (client) {
      form.setFieldsValue({
        id: client.id,
        name: client.name,
        email: client.email,
        phone: client.phone,
        locationId: client.locationIds?.[0],
        timeAtStop: client.timeAtStop,
        arrivalTimes: client.arrivalTimes,
      })
    }
  }, [client])

  useEffect(() => {
    setIsModalVisible(isOpen)
  }, [isOpen])

  return (
    <>
      {React.cloneElement(button, { onClick: openModal })}
      <ModalCard closable isModalVisible={isModalVisible} onCancel={handleClose} title={title}>
        <Form
          className="flex flex-col pt-2"
          id={"create-api-keys-form"}
          form={form}
          onFinish={onFinish}
        >
          {
            <div>
              <SenderNameFormItem
                name="name"
                placeholder={t("common.fullName")}
                label={t("common.fullName")}
                validationErrorText={t("common.forms.enterName")}
                disabled={!!createdClient}
              />
              <EmailFormItem
                name="email"
                placeholder={`${t("common.forms.emailAddress")} (${t("common.optional")})`}
              />
              <PhoneFormItem
                handlePhoneSelect={handlePhoneSelect}
                countryCode={countryCode || "SG"}
                skipValidation={true}
              />
              <label className="text-sm text-gray-500" htmlFor="pickup-location">
                {t("common.location")}
              </label>
              <DraftPickupCell
                onSelect={handleLocationSelect}
                defaultLocationId={client?.locationIds?.[0]}
                label={t("common.selectLocation")}
              />
              <div className="max-w-[350px] mt-4">
                <DayTimeRangeForm
                  name="expectedArrival"
                  label={t("create-order.DropoffForm.dropoffByLabel")}
                  timeWindow={{
                    from: client?.arrivalTimes?.start
                      ? DateTime.fromISO(client?.arrivalTimes?.start)
                      : undefined,
                    to: client?.arrivalTimes?.end
                      ? DateTime.fromISO(client?.arrivalTimes?.end)
                      : undefined,
                  }}
                  form={form}
                  setFieldsChange={() => {}}
                />
              </div>
              <TimeAtStopFormItem
                name="timeAtStop"
                label={t("create-order.DropoffForm.expectedTimeAtStop")}
              />
              {!isEmpty(createdClient) && (
                <div className="flex flex-col space-y-4">
                  <div className="flex w-full px-4 py-2 space-x-2 bg-[#F9FAFB]">
                    <FontAwesomeIcon
                      className="text-gray-600 pt-1"
                      size="lg"
                      icon={faExclamationCircle}
                    />
                    <p id={"save-key-tips-text"}>{t("common.portalLink")}</p>
                  </div>
                  <div className="flex w-full rounded border px-4 py-3">
                    <Paragraph
                      id={"api-secret-key"}
                      className="text-gray-400 text-base break-all"
                      copyable={{
                        icon: (
                          <FontAwesomeIcon
                            className="text-gray-500 text-md cursor-pointer"
                            icon={faClone}
                          />
                        ),
                      }}
                    >
                      {createdClient?.url}
                    </Paragraph>
                  </div>
                </div>
              )}
            </div>
          }
          <div className="flex w-full justify-end !mt-4 space-x-4">
            {isEmpty(createdClient) ? (
              <KosmoButton id="create-api-key-form-btn" type="primary" htmlType="submit">
                {!!client ? t("common.save") : t("common.create")}
              </KosmoButton>
            ) : (
              <KosmoButton id="api-form-close-button" type="primary" onClick={handleClose}>
                {t("common.close")}
              </KosmoButton>
            )}
          </div>
        </Form>
      </ModalCard>
    </>
  )
}

export default ClientsAddEditModal
